import { useState, useEffect, useRef, useCallback } from "react";
import "../../CSS/PartyInvoice.css";

import useDraggableScroll from "../../hooks/useDraggableScroll";
import api from "../../../src/api.js";
import saamiLogo from "../../Img/saami_logo_256x297.jpg";



/* ═════════
═══════════════════════════════════
   CONSTANTS & DATA
════════════════════════════════════════════ */
const COMPANY_GSTIN = "27AABCS1234A1ZP";
const COMPANY_STATE = "27";

/* PARTIES / PLACE_NAMES / STATE_NAMES removed — data now comes from backend API */

/* INITIAL_INVOICES removed — data now loaded from /api/bill-summary */

/* ════════════════════════════════════════════
   UTILITIES
════════════════════════════════════════════ */
function fmt(n) {
  const v = Number(n);
  return (Number.isFinite(v) ? v : 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
/* Formats a Date using its LOCAL calendar date (no UTC shift).
   new Date().toISOString() converts to UTC first, which silently rolls
   the date back by one day for any timezone ahead of UTC (e.g. IST,
   UTC+5:30) whenever the local time is before the UTC offset catches up
   — e.g. any time before 5:30am IST, or when just constructing a local
   month/week boundary like "last day of last month". Always use this
   instead of new Date(...).toISOString().slice(0,10) for calendar dates. */
function toLocalISO(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
const todayISO = () => toLocalISO(new Date());
function numberToWords(n) {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  function words(num) {
    if (num === 0) return "";
    if (num < 20) return ones[num] + " ";
    if (num < 100) return tens[Math.floor(num / 10)] + " " + (num % 10 ? ones[num % 10] + " " : "");
    if (num < 1000) return ones[Math.floor(num / 100)] + " Hundred " + (num % 100 ? words(num % 100) : "");
    if (num < 100000) return words(Math.floor(num / 1000)) + "Thousand " + (num % 1000 ? words(num % 1000) : "");
    if (num < 10000000) return words(Math.floor(num / 100000)) + "Lakh " + (num % 100000 ? words(num % 100000) : "");
    return words(Math.floor(num / 10000000)) + "Crore " + (num % 10000000 ? words(num % 10000000) : "");
  }
  const whole = Math.floor(n);
  const paise = Math.round((n - whole) * 100);
  let result = words(whole).trim() + " Rupees";
  if (paise > 0) result += " and " + words(paise).trim() + " Paise";
  return result + " Only";
}

function getGstType(partyGstin) {
  if (!partyGstin || partyGstin.length < 2) return "IGST";
  return partyGstin.slice(0, 2) === COMPANY_STATE ? "CGST+SGST" : "IGST";
}

function calcGst(taxable, rate, gstType) {
  const gstAmt = +(taxable * rate / 100).toFixed(2);
  if (gstType === "CGST+SGST") {
    return { cgst: +(gstAmt / 2).toFixed(2), sgst: +(gstAmt / 2).toFixed(2), igst: 0, total: gstAmt };
  }
  return { cgst: 0, sgst: 0, igst: gstAmt, total: gstAmt };
}

/* ════════════════════════════════════════════
   SUB-COMPONENTS
════════════════════════════════════════════ */

function Header({ view }) {
  return (
    <header className="hdr">
      <div className="logo">
        <div className="logo-mark">
          <img src={saamiLogo} alt="Saami Tradestar Logistics" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </div>
        <div>
          <div className="logo-name">Saami <span>Tradestar</span> Logistics</div>
          <div className="logo-pvt">Pvt Ltd</div>
        </div>
      </div>
      <div className="hdr-r">
        <span className="hdr-pill">{view === "list" ? "Invoice Register" : "New Invoice"}</span>
        <div className="hdr-user">
          <div className="hdr-av">RM</div>
          Rahul M.
        </div>
      </div>
    </header>
  );
}

function Breadcrumb({ view }) {
  return (
    <div className="bc">
      <span>Finance</span>
      <span className="bc-sep">›</span>
      <span>Party Invoice</span>
      <span className="bc-sep">›</span>
      <span className="bc-cur">{view === "list" ? "Invoice Register" : "New Invoice"}</span>
    </div>
  );
}

function ToastContainer({ toasts }) {
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.type}`}>
          <span style={{ fontSize: 14 }}>{t.type === "ok" ? "✓" : t.type === "err" ? "✕" : t.type === "warn" ? "⚠" : "ℹ"}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ── STATUS PILL ── */
function StatusPill({ status }) {
  const map = {
    paid: { cls: "pill-paid", label: "✓ Paid" },
    draft: { cls: "pill-draft", label: "Draft" },
    sent: { cls: "pill-sent", label: "● Sent" },
    overdue: { cls: "pill-overdue", label: "⚠ Overdue" },
  };
  const s = map[status] || map.draft;
  return <span className={`pill ${s.cls}`}>{s.label}</span>;
}

/* ── LIST VIEW ── */
function fmtLakh(n) {
  if (n >= 10000000) return "₹" + (n / 10000000).toFixed(2) + "Cr";
  if (n >= 100000)   return "₹" + (n / 100000).toFixed(2) + "L";
  if (n >= 1000)     return "₹" + (n / 1000).toFixed(1) + "K";
  return "₹" + (n || 0).toFixed(0);
}
function ListView({ invoices, loading, loadError, onNew, onOpen, onOpenPdf, addToast,
                    branchList, filterBranch, setFilterBranch,
                    filterFrom, setFilterFrom, filterTo, setFilterTo, onRefresh }) {
  /* ── Derive stat tile values from live data ── */
  const now = new Date();
  const thisMonth = invoices.filter((inv) => {
    if (!inv.bill_Period) return false;
    const parts = inv.bill_Period.split(" - ");
    const d = new Date(parts[0]);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  });
  const totalInvoiced = invoices.reduce((s, inv) => s + (inv.bill_Total || 0), 0);
  const totalFreight  = invoices.reduce((s, inv) => s + (inv.freight_Charges || 0), 0);
  const totalCNRs     = invoices.reduce((s, inv) => s + (inv.no_Of_Consignments || 0), 0);

  /* ── Pagination ── */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(invoices.length / itemsPerPage) || 1;
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pagedInvoices = invoices.slice(pageStart, pageStart + itemsPerPage);

  // Reset to page 1 whenever the loaded result set changes (new search/refresh).
  useEffect(() => {
    setCurrentPage(1);
  }, [invoices.length, filterBranch, filterFrom, filterTo]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  return (
    <div className="page">
      <div className="pg-row">
        <div className="pg-title">
          <h1>Party Invoice Generation</h1>
          <p>Generate GST-compliant freight invoices per party with automated tax calculation</p>
        </div>
        <div className="pg-acts">
          <button className="btn btn-primary btn-lg" onClick={onNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Invoice
          </button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="stat-row">
        <div className="stat-box blue">
          <div className="sb-val">{loading ? "…" : thisMonth.length}</div>
          <div className="sb-lbl">This Month</div>
        </div>
        <div className="stat-box green">
          <div className="sb-val">{loading ? "…" : fmtLakh(totalInvoiced)}</div>
          <div className="sb-lbl">Invoiced Amount</div>
        </div>
        <div className="stat-box amber">
          <div className="sb-val">{loading ? "…" : totalCNRs}</div>
          <div className="sb-lbl">Total CNRs</div>
        </div>
        <div className="stat-box navy">
          <div className="sb-val">{loading ? "…" : fmtLakh(totalFreight)}</div>
          <div className="sb-lbl">Freight Charges</div>
        </div>
      </div>

      {/* Invoice register */}
      <div className="card">
        <div className="band">
          <div className="band-icon">
            <svg viewBox="0 0 24 24" strokeWidth="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div>
            <div className="band-title">Invoice Register</div>
            <div className="band-sub">All generated invoices — click to view or edit</div>
          </div>
          <div className="band-r">
            <span style={{ background: "rgba(255,255,255,.18)", border: "1px solid rgba(255,255,255,.25)", borderRadius: 6, padding: "3px 11px", fontSize: 12, fontWeight: 700, color: "#fff" }}>
              {loading ? "Loading…" : `${invoices.length} records`}
            </span>
            <button className="btn-ghost btn btn-sm" style={{ background: "rgba(255,255,255,.1)", color: "rgba(255,255,255,.85)", borderColor: "rgba(255,255,255,.2)" }} onClick={() => addToast("Exporting…", "ok")}>
              Export
            </button>
          </div>




{/* ── Filter Bar ── */}


    <div style={{ display: "flex", alignItems: "flex-end", gap: 12, padding: "14px 16px", borderBottom: "1px solid var(--border)", background: "var(--s2)", flexWrap: "wrap", width: "100%", boxSizing: "border-box" }}>
  <div className="field" style={{ margin: 0, flex: "2 1 220px", minWidth: 0 }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>Billing Branch</label>
    <select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
      <option value="">— All Branches —</option>
      {branchList.map((b) => (
        <option key={b.branch_id} value={b.branch_id}>{b.branch_Name}</option>
      ))}
    </select>
  </div>
<div className="field" style={{ margin: 0, flex: "1 1 160px", minWidth: 0 }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>From Date</label>
    <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} style={{ width: "100%" }} />
  </div>
  <div className="field" style={{ margin: 0, flex: "1 1 160px", minWidth: 0 }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>To Date</label>
    <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} style={{ width: "100%" }} />
  </div>
  <button
    className="btn btn-primary btn-sm"
    onClick={onRefresh}
    disabled={loading}
    style={{ height: 36, paddingInline: 18, flexShrink: 0 }}
  >
    {loading ? "⏳ Loading…" : (
      <>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
          <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
        </svg>
        Search
      </>
    )}
  </button>
  <button
    className="btn btn-ghost btn-sm"
    onClick={() => {
      setFilterBranch("");
      setFilterFrom(toLocalISO(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
      setFilterTo(todayISO());
    }}
    style={{ height: 36, flexShrink: 0 }}
  >
    ↺ Reset
  </button>
</div>
        </div>


          






        {/* Loading state */}
        {loading && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--muted)" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
            <div style={{ fontWeight: 600 }}>Loading invoice register…</div>
          </div>
        )}

        {/* Error state */}
        {!loading && loadError && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--red)" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⚠</div>
            <div style={{ fontWeight: 600 }}>Failed to load invoices</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>{loadError}</div>
          </div>
        )}

        {/* Table */}
        {!loading && !loadError && (
          <div className="tbl-wrap">
            <table className="inv-list-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }} className="center">#</th>
                  <th>Invoice No.</th>
                  <th>Party</th>
                  <th>GSTIN</th>
                  <th>Period</th>
                  <th className="center">CNRs</th>
                  <th className="right">Freight (₹)</th>
                  <th className="right">Total (₹)</th>
                  <th className="center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", padding: "32px", color: "var(--muted)" }}>
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  pagedInvoices.map((inv, i) => (
                    // <tr key={inv.bill_No || i} onClick={() => onOpen(inv.bill_No)}>
                      <tr key={inv.bill_No || i} onClick={() => onOpen(inv.bill_Code)}>
                      <td className="center" style={{ color: "var(--muted)", fontSize: 12 }}>{pageStart + i + 1}</td>
                      <td><span className="inv-link">{inv.bill_No}</span></td>
                      <td>
                        <div style={{ fontWeight: 700, color: "var(--navy)" }}>{inv.party_Name || "—"}</div>
                      </td>
                      <td style={{ fontSize: 12, color: "var(--muted)" }}>{inv.gstin || "—"}</td>
                      <td style={{ fontSize: 12, color: "var(--blue)" }}>{inv.bill_Period || "—"}</td>
                      <td className="center"><strong style={{ color: "var(--blue)" }}>{inv.no_Of_Consignments ?? "—"}</strong></td>
                      <td className="right"><strong style={{ color: "var(--navy)" }}>₹ {fmt(inv.freight_Charges || 0)}</strong></td>
                      <td className="right" style={{ fontWeight: 800, color: "var(--teal)" }}>₹ {fmt(inv.bill_Total || 0)}</td>
                      <td className="center">
                        <div style={{ display: "flex", gap: 5, justifyContent: "center" }}>
                          <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onOpen(inv.bill_Code); }} style={{ padding: "5px 8px" }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onOpenPdf(inv.bill_Code); }} style={{ padding: "5px 8px", color: "var(--red)", borderColor: "rgba(220,38,38,.25)" }} title="View PDF">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !loadError && invoices.length > 0 && totalPages > 1 && (
          <div className="pagination">
            <div className="pg-info">
              Showing {pageStart + 1} to {Math.min(pageStart + itemsPerPage, invoices.length)} of {invoices.length} entries
            </div>
            <div className="pg-btns">
              <button
                className="pg-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, idx) => (
                <button
                  key={idx + 1}
                  className={`pg-btn ${currentPage === idx + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                className="pg-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", background: "var(--s3)", fontSize: 12, color: "var(--muted)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Showing {invoices.length === 0 ? 0 : pageStart + 1}–{Math.min(pageStart + itemsPerPage, invoices.length)} of {invoices.length} invoices</span>
          <span>Click Invoice No. or eye icon to view details</span>
        </div>
      </div>
    </div>
  );
}

/* ── INVOICE SUMMARY SIDEBAR ── */
function InvoiceSummary({ bookings, gstRate, gstType, invNo, generating, onGenerate, onPrint, onPdf, regenerateLocked }) {
  const sel = bookings.filter((b) => b.selected);
  const sPcs = sel.reduce((a, b) => a + b.pcs, 0);
  const sWt = sel.reduce((a, b) => a + b.wt, 0);
  const sFreight = sel.reduce((a, b) => a + b.freight, 0);
  const sFuel = sel.reduce((a, b) => a + b.fuel, 0);
  const sOther = sel.reduce((a, b) => a + b.other, 0);
  const sSub = sel.reduce((a, b) => a + b.subtotal, 0);
  const gst = calcGst(sSub, gstRate, gstType || "IGST");
  const invTotal = +(sSub + gst.total).toFixed(2);
  const isIntra = gstType === "CGST+SGST";

  return (
    <div className="inv-summary">
      <div className="is-band">
        <div className="band-icon">
          <svg viewBox="0 0 24 24" strokeWidth="2">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
          </svg>
        </div>
        <div className="is-band-title">Invoice Summary</div>
      </div>

      <div className="is-body">
        <div className="is-row"><span className="is-lbl">Consignments</span><span className="is-val" style={{ color: "var(--blue)" }}>{sel.length}</span></div>
        <div className="is-row"><span className="is-lbl">Total Pieces</span><span className="is-val" style={{ color: "var(--blue)" }}>{sPcs}</span></div>
        <div className="is-row"><span className="is-lbl">Charged Weight</span><span className="is-val" style={{ color: "var(--teal)" }}>{sWt.toFixed(2)} kg</span></div>
        <div className="is-row is-sub"><span className="is-lbl">Freight Charges</span><span className="is-val">₹ {fmt(sFreight)}</span></div>
        <div className="is-row"><span className="is-lbl">Fuel Surcharge</span><span className="is-val" style={{ color: "var(--slate)" }}>₹ {fmt(sFuel)}</span></div>
        <div className="is-row"><span className="is-lbl">Other Charges</span><span className="is-val" style={{ color: "var(--slate)" }}>₹ {fmt(sOther)}</span></div>
        <div className="is-row is-sub"><span className="is-lbl">Taxable Amount</span><span className="is-val">₹ {fmt(sSub)}</span></div>

        {/* GST Breakdown */}
        <div className="is-gst-split" style={{ background: isIntra ? "var(--blue-lt)" : "var(--amber-lt)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: isIntra ? "var(--blue)" : "var(--amber)", marginBottom: 5 }}>
            GST Breakdown ({isIntra ? "Intra" : "Inter"}-State @ {gstRate}%)
          </div>
          {isIntra ? (
            <>
              <div className="is-gst-row"><span className="is-gst-lbl">CGST @ {gstRate / 2}%</span><span className="is-gst-val">₹ {fmt(gst.cgst)}</span></div>
              <div className="is-gst-row"><span className="is-gst-lbl">SGST @ {gstRate / 2}%</span><span className="is-gst-val">₹ {fmt(gst.sgst)}</span></div>
            </>
          ) : (
            <div className="is-gst-row"><span className="is-gst-lbl">IGST @ {gstRate}%</span><span className="is-gst-val" style={{ color: "var(--amber)" }}>₹ {fmt(gst.igst)}</span></div>
          )}
        </div>

        <div className="is-row"><span className="is-lbl">Total GST</span><span className="is-val" style={{ color: "var(--blue)" }}>₹ {fmt(gst.total)}</span></div>
        <div className="is-row is-total">
          <span className="is-lbl">Invoice Total</span>
          <span className="is-val">₹ {fmt(invTotal)}</span>
        </div>

        <div style={{ padding: "10px 0 6px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--blue)", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".4px" }}>Amount in Words</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", lineHeight: 1.5, background: "var(--blue-lt)", borderRadius: 6, padding: "8px 10px", border: "1px solid var(--border)" }}>
            {sel.length > 0 ? numberToWords(invTotal) : "—"}
          </div>
        </div>
      </div>

      <div style={{ padding: "14px 16px", borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 8 }}>
        {regenerateLocked && (
          <div className="warn-inline">
            ⚠ Please delete this bill before regenerate
          </div>
        )}
        <button className="btn-full-green" onClick={onGenerate} disabled={generating || regenerateLocked}>
          {generating ? (
            <>⏳ Generating…</>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Generate Invoice
            </>
          )}
        </button>
        <div className="g2">
          <button className="btn btn-ghost btn-sm btn-block" onClick={onPrint}>🖨 Print</button>
          <button className="btn btn-ghost btn-sm btn-block" onClick={onPdf}>📄 PDF</button>
        </div>
      </div>
    </div>
  );
}

/* ── CONSIGNMENT TABLE ── */
function ConsignmentTable({ bookings, onToggleRow, onToggleAll, tblFilter, setTblFilter, searchQ, setSearchQ }) {
  const sel = bookings.filter((b) => b.selected);
  const allChecked = bookings.length > 0 && bookings.every((b) => b.selected);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(bookings.length / itemsPerPage) || 1;
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pagedBookings = bookings.slice(pageStart, pageStart + itemsPerPage);

  // Reset to page 1 whenever the result set or active filter/search changes
  // (not on a plain row-selection toggle, since that doesn't change length).
  useEffect(() => {
    setCurrentPage(1);
  }, [bookings.length, tblFilter, searchQ]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const sPcs = sel.reduce((a, b) => a + b.pcs, 0);
  const sWt = sel.reduce((a, b) => a + b.wt, 0);
  const sFreight = sel.reduce((a, b) => a + b.freight, 0);
  const sOther = sel.reduce((a, b) => a + b.other, 0);
  const sSub = sel.reduce((a, b) => a + b.subtotal, 0);
  const aPcs = bookings.reduce((a, b) => a + b.pcs, 0);
  const aWt = bookings.reduce((a, b) => a + b.wt, 0);
  const aFreight = bookings.reduce((a, b) => a + b.freight, 0);
  const aOther = bookings.reduce((a, b) => a + b.other, 0);
  const aSub = bookings.reduce((a, b) => a + b.subtotal, 0);

  return (
    <div className="card">
      <div className="band">
        <div className="band-icon">
          <svg viewBox="0 0 24 24" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <polyline points="9 11 12 14 22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
        </div>
        <div>
          <div className="band-title">Consignments</div>
          <div className="band-sub">{bookings.length} consignment{bookings.length !== 1 ? "s" : ""} loaded</div>
        </div>
        <div className="band-r">
          <span style={{ background: "rgba(255,255,255,.18)", border: "1px solid rgba(255,255,255,.25)", borderRadius: 6, padding: "3px 11px", fontSize: 12, fontWeight: 700, color: "#fff" }}>
            {sel.length} selected
          </span>
          <button className="btn-ghost btn btn-sm" style={{ background: "rgba(255,255,255,.1)", color: "rgba(255,255,255,.85)", borderColor: "rgba(255,255,255,.2)" }} onClick={() => onToggleAll(!allChecked)}>
            {allChecked ? "Deselect All" : "Select All"}
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", background: "var(--s2)", display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
        {[["all", "All", ""], ["pod", "✓ POD Available", "f-pod"], ["nopod", "⏳ Awaiting POD", "f-no-pod"]].map(([key, label, extra]) => (
          <button key={key} className={`fchip ${extra} ${tblFilter === key ? "active" : ""}`} onClick={() => setTblFilter(key)}>
            {label}
          </button>
        ))}
        <div style={{ marginLeft: "auto" }}>
          <input
            type="text"
            placeholder="🔍 Search CNR…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            style={{ padding: "6px 11px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12.5, fontFamily: "inherit", width: 200, outline: "none", color: "var(--slate)" }}
          />
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="cnt-table">
          <thead>
            <tr>
              <th className="center" style={{ width: 36 }}>
                <input type="checkbox" className="chk" checked={allChecked} onChange={(e) => onToggleAll(e.target.checked)} />
              </th>
              <th>#</th>
              <th>CNR No.</th>
              <th>Date</th>
              <th>Origin</th>
              <th>Destination</th>
              <th className="center">Pcs</th>
              <th className="right">Chg Wt (kg)</th>
              <th className="right">Freight (₹)</th>
              <th className="right">Other (₹)</th>
              <th className="right">Subtotal (₹)</th>
              <th className="center">POD</th>
              <th className="center">Status</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan="13">
                  <div className="empty-state" style={{ padding: 32 }}>
                    <div className="empty-icon">📦</div>
                    <div className="empty-title">No consignments match</div>
                    <div className="empty-sub">Adjust filters or date range</div>
                  </div>
                </td>
              </tr>
            ) : (
              pagedBookings.map((b, i) => (
                <tr key={b.id} className={b.selected ? "sel-row" : ""}>
                  <td className="center">
                    <input type="checkbox" className="chk" checked={b.selected} onChange={(e) => onToggleRow(pageStart + i, e.target.checked)} />
                  </td>
                  <td className="muted">{pageStart + i + 1}</td>
                  <td>
                    <div className="cnr-code">{b.cnr}</div>
                    <div className="cnr-date">{b.dateDisp}</div>
                  </td>
                  <td className="muted">{b.dateDisp}</td>
                  <td style={{ fontSize: 12, fontWeight: 700, color: "var(--blue)" }}>{b.from}</td>
                  <td style={{ fontSize: 12, fontWeight: 700, color: "var(--blue)" }}>{b.to}</td>
                  <td className="center"><strong style={{ color: "var(--blue)" }}>{b.pcs}</strong></td>
                  <td className="right" style={{ color: "var(--teal)", fontWeight: 600 }}>{b.wt.toFixed(2)}</td>
                  <td className="right" style={{ color: "var(--navy)", fontWeight: 700 }}>{fmt(b.freight)}</td>
                  <td className="right" style={{ color: "var(--muted)" }}>{b.other > 0 ? fmt(b.other) : "—"}</td>
                  <td className="right" style={{ fontWeight: 800, color: "var(--teal)" }}>{fmt(b.subtotal)}</td>
                  <td className="center">
                    {b.pod ? <span className="pod-yes">✓ POD</span> : <span className="pod-no">⏳ Pending</span>}
                  </td>
                  <td className="center">
                    <span style={{
                      fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                      background: b.status === "Delivered" ? "var(--green-lt)" : b.status === "In Transit" ? "var(--blue-lt)" : "var(--s3)",
                      color: b.status === "Delivered" ? "var(--green)" : b.status === "In Transit" ? "var(--blue)" : "var(--muted)",
                    }}>
                      {b.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {bookings.length > 0 && (
            <tfoot>
              <tr className="row-sub">
                <td colSpan="6" style={{ textAlign: "left", paddingLeft: 12 }}>SELECTED TOTAL</td>
                <td className="center" style={{ color: "var(--blue)" }}>{sPcs}</td>
                <td className="right" style={{ color: "var(--teal)" }}>{sWt.toFixed(2)} kg</td>
                <td className="right" style={{ color: "var(--navy)" }}>₹ {fmt(sFreight)}</td>
                <td className="right">{sOther > 0 ? "₹ " + fmt(sOther) : "—"}</td>
                <td className="right" style={{ color: "var(--teal)", fontSize: 13.5, fontWeight: 800 }}>₹ {fmt(sSub)}</td>
                <td colSpan="2"></td>
              </tr>
              <tr className="row-grand">
                <td colSpan="6" style={{ textAlign: "left", paddingLeft: 12 }}>
                  GRAND TOTAL — <span className="muted-w">{bookings.length} consignment{bookings.length !== 1 ? "s" : ""}</span>
                </td>
                <td className="center" style={{ color: "#93c5fd", fontSize: 14 }}>{aPcs}</td>
                <td className="right" style={{ color: "rgba(255,255,255,.85)" }}>{aWt.toFixed(2)} kg</td>
                <td className="right" style={{ color: "rgba(255,255,255,.85)" }}>₹ {fmt(aFreight)}</td>
                <td className="right" style={{ color: "rgba(255,255,255,.75)" }}>{aOther > 0 ? "₹ " + fmt(aOther) : "—"}</td>
                <td className="right" style={{ color: "var(--teal)", fontWeight: 800 }}>₹ {fmt(aSub)}</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          )}
        </table>

        {bookings.length > 0 && totalPages > 1 && (
          <div className="pagination">
            <div className="pg-info">
              Showing {pageStart + 1} to {Math.min(pageStart + itemsPerPage, bookings.length)} of {bookings.length} entries
            </div>
            <div className="pg-btns">
              <button
                className="pg-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, idx) => (
                <button
                  key={idx + 1}
                  className={`pg-btn ${currentPage === idx + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                className="pg-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── PDF PREVIEW MODAL ── */
function PdfModal({ open, onClose, consignments, partyId, partyName, partyDetails, gstType, gstRate, generatedBill, onPrint }) {
  if (!open || !partyId || !generatedBill) return null;

  const isIntra  = gstType === "CGST+SGST";
  const taxable  = generatedBill.taxable;
  const invTotal = generatedBill.total;
  const today    = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  function printInvoice() {
    const rowsHtml = consignments.map((b, i) => `
      <tr>
        <td class="muted">${i + 1}</td>
        <td class="blue" style="font-weight:700">${b.cnr}</td>
        <td class="muted" style="font-size:11px">${b.dateDisp}</td>
        <td class="muted">${b.from} &rarr; ${b.to}</td>
        <td style="text-align:center">${b.pcs}</td>
        <td class="teal" style="text-align:right">${b.wt.toFixed(2)}</td>
        <td style="text-align:right">&#8377; ${fmt(b.freight)}</td>
        <td class="muted" style="text-align:right">${b.other > 0 ? "&#8377; " + fmt(b.other) : "&mdash;"}</td>
        <td class="teal" style="text-align:right;font-weight:700">&#8377; ${fmt(b.subtotal)}</td>
      </tr>`).join("");

    const totalsRows = [
      ["Freight Charges", "&#8377; " + fmt(generatedBill.freight)],
      ...(generatedBill.fuel  > 0 ? [["Fuel Surcharge", "&#8377; " + fmt(generatedBill.fuel)]]  : []),
      ...(generatedBill.other > 0 ? [["Other Charges",  "&#8377; " + fmt(generatedBill.other)]] : []),
      ["Taxable Amount", "&#8377; " + fmt(taxable)],
      ...(isIntra
        ? [["CGST @ " + gstRate / 2 + "%", "&#8377; " + fmt(generatedBill.cgst)],
           ["SGST @ " + gstRate / 2 + "%", "&#8377; " + fmt(generatedBill.sgst)]]
        : [["IGST @ " + gstRate + "%",     "&#8377; " + fmt(generatedBill.igst)]]),
    ].map(([l, v]) => `
      <div class="totrow"><span class="muted">${l}</span><span style="font-weight:700">${v}</span></div>
    `).join("");

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Tax Invoice — ${generatedBill.billNo}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #1e293b; margin: 0; padding: 0; }
  .muted { color: #64748b; }
  .teal  { color: #0d9488; }
  .blue  { color: #2563eb; }
  .navy  { color: #1e293b; }
  .hd { display:flex; justify-content:space-between; border-bottom:3px solid #0d9488; padding-bottom:12px; margin-bottom:14px; }
  .hd .left { display:flex; align-items:center; gap:12px; }
  .hd .logo-img { width:56px; height:auto; object-fit:contain; }
  .hd .company { font-size:16px; font-weight:800; color:#0d9488; }
  .hd .addr { font-size:11px; color:#64748b; margin-top:3px; line-height:1.5; }
  .hd .right { text-align:right; }
  .hd .tag { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.8px; color:#64748b; }
  .hd .no { font-size:20px; font-weight:800; color:#0d9488; }
  .hd .meta { font-size:11px; color:#64748b; margin-top:4px; }
  .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:12px; }
  .info-box { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:10px 12px; }
  .info-box .lbl { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.6px; color:#2563eb; margin-bottom:6px; }
  .info-box .party { font-weight:700; font-size:13.5px; color:#2563eb; }
  .info-box .line { color:#64748b; font-size:12px; margin-top:3px; }
  .inforow { display:flex; justify-content:space-between; font-size:12px; padding:2px 0; }
  table.cons { width:100%; border-collapse:collapse; font-size:12px; margin-bottom:12px; }
  table.cons thead tr { background:#eef2ff; }
  table.cons th { padding:7px 9px; font-size:10px; font-weight:700; text-transform:uppercase; color:#1e293b; border-bottom:2px solid #e2e8f0; text-align:left; }
  table.cons th.r { text-align:right; }
  table.cons th.c { text-align:center; }
  table.cons td { padding:7px 9px; border-bottom:1px solid #e2e8f0; }
  .totals { float:right; width:300px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:12px; }
  .totrow { display:flex; justify-content:space-between; font-size:12.5px; padding:4px 0; border-bottom:1px solid #e2e8f0; }
  .grand { display:flex; justify-content:space-between; font-size:14px; padding:8px 0 0; border-top:2px solid #0d9488; margin-top:4px; font-weight:800; color:#0d9488; }
  .footnote { font-size:11.5px; color:#64748b; margin-top:16px; padding-top:10px; border-top:1px solid #e2e8f0; clear:both; }
</style>
</head>
<body>
  <div class="hd">
    <div class="left">
      <img class="logo-img" src="${new URL(saamiLogo, window.location.origin).href}" alt="Saami Tradestar Logistics" />
      <div>
        <div class="company">SAAMI TRADESTAR LOGISTICS PVT LTD</div>
        <div class="addr">Mumbai Central Hub, MIDC, Andheri East, Mumbai &mdash; 400093<br />GSTIN: ${COMPANY_GSTIN} | Phone: +91-22-12345678</div>
      </div>
    </div>
    <div class="right">
      <div class="tag">TAX INVOICE</div>
      <div class="no">${generatedBill.billNo}</div>
      <div class="meta">Date: ${today}</div>
      <div class="meta">Bill ID: #${generatedBill.billId}</div>
    </div>
  </div>

  <div class="info-grid">
    <div class="info-box">
      <div class="lbl">Billed To</div>
      <div class="party">${partyName}</div>
      <div class="line">GSTIN: ${partyDetails?.gsT_Number || "&mdash;"}</div>
      <div class="line">${partyDetails?.place_Name || ""}${partyDetails?.state_Name ? ", " + partyDetails.state_Name : ""}</div>
    </div>
    <div class="info-box">
      <div class="lbl">Invoice Info</div>
      <div class="inforow"><span class="muted">Invoice Date</span><span style="font-weight:700">${today}</span></div>
      <div class="inforow"><span class="muted">GST Type</span><span style="font-weight:700">${gstType}</span></div>
      <div class="inforow"><span class="muted">GST Rate</span><span style="font-weight:700">${gstRate}%</span></div>
      <div class="inforow"><span class="muted">Consignments</span><span style="font-weight:700">${consignments.length}</span></div>
    </div>
  </div>

  <table class="cons">
    <thead>
      <tr>
        <th>#</th><th>CNR No.</th><th>Date</th><th>Route</th><th class="c">Pcs</th>
        <th class="r">Wt (kg)</th><th class="r">Freight (₹)</th><th class="r">Other (₹)</th><th class="r">Amount (₹)</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>

  <div class="totals">
    ${totalsRows}
    <div class="grand"><span>Invoice Total</span><span>&#8377; ${fmt(invTotal)}</span></div>
  </div>

  <div class="footnote">
    <strong>Amount in Words:</strong> ${numberToWords(invTotal)}<br />
    <strong>Note:</strong> GST registration: ${COMPANY_GSTIN}. This is a computer-generated invoice.
  </div>

  <script>
    window.onload = function () { window.print(); };
  </script>
</body>
</html>`;

    const win = window.open("", "_blank", "width=850,height=1100");
    if (!win) {
      if (onPrint) onPrint();
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  }

  return (
    <div className="overlay show" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <h3>Tax Invoice — {generatedBill.billNo}</h3>
          <button className="modal-x" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "3px solid var(--teal)", paddingBottom: 12, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src={saamiLogo} alt="Saami Tradestar Logistics" style={{ width: 56, height: "auto", objectFit: "contain" }} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "var(--teal)" }}>SAAMI TRADESTAR LOGISTICS PVT LTD</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3, lineHeight: 1.5 }}>
                  Mumbai Central Hub, MIDC, Andheri East, Mumbai — 400093<br />
                  GSTIN: {COMPANY_GSTIN} | Phone: +91-22-12345678
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".8px", color: "var(--muted)" }}>TAX INVOICE</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "var(--teal)" }}>{generatedBill.billNo}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>Date: {today}</div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>Bill ID: #{generatedBill.billId}</div>
            </div>
          </div>

          {/* Billed to / Invoice info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 6, padding: "10px 12px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--blue)", marginBottom: 6 }}>Billed To</div>
              <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--blue)" }}>{partyName}</div>
              <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 3 }}>GSTIN: {partyDetails?.gsT_Number || "—"}</div>
              <div style={{ color: "var(--muted)", fontSize: 12 }}>{partyDetails?.place_Name || ""}{partyDetails?.state_Name ? `, ${partyDetails.state_Name}` : ""}</div>
            </div>
            <div style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 6, padding: "10px 12px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--blue)", marginBottom: 6 }}>Invoice Info</div>
              {[
                ["Invoice Date", today],
                ["GST Type", gstType],
                ["GST Rate", gstRate + "%"],
                ["Consignments", consignments.length],
              ].map(([l, v]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                  <span style={{ color: "var(--muted)" }}>{l}</span>
                  <span style={{ fontWeight: 700 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Consignment lines */}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 12 }}>
            <thead>
              <tr style={{ background: "#eef2ff" }}>
                {["#", "CNR No.", "Date", "Route", "Pcs", "Wt (kg)", "Freight (₹)", "Other (₹)", "Amount (₹)"].map((h) => (
                  <th key={h} style={{ padding: "7px 9px", textAlign: ["Pcs"].includes(h) ? "center" : ["Wt (kg)", "Freight (₹)", "Other (₹)", "Amount (₹)"].includes(h) ? "right" : "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--navy)", borderBottom: "2px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {consignments.map((b, i) => (
                <tr key={b.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "7px 9px", color: "var(--muted)" }}>{i + 1}</td>
                  <td style={{ padding: "7px 9px", fontWeight: 700, color: "var(--blue)" }}>{b.cnr}</td>
                  <td style={{ padding: "7px 9px", color: "var(--muted)", fontSize: 11 }}>{b.dateDisp}</td>
                  <td style={{ padding: "7px 9px", color: "var(--muted)" }}>{b.from} → {b.to}</td>
                  <td style={{ padding: "7px 9px", textAlign: "center" }}>{b.pcs}</td>
                  <td style={{ padding: "7px 9px", textAlign: "right", color: "var(--teal)" }}>{b.wt.toFixed(2)}</td>
                  <td style={{ padding: "7px 9px", textAlign: "right" }}>₹ {fmt(b.freight)}</td>
                  <td style={{ padding: "7px 9px", textAlign: "right", color: "var(--muted)" }}>{b.other > 0 ? "₹ " + fmt(b.other) : "—"}</td>
                  <td style={{ padding: "7px 9px", textAlign: "right", fontWeight: 700, color: "var(--teal)" }}>₹ {fmt(b.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ float: "right", width: 300, background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 6, padding: 12 }}>
            {[
              ["Freight Charges",    "₹ " + fmt(generatedBill.freight)],
              ...(generatedBill.fuel  > 0 ? [["Fuel Surcharge",   "₹ " + fmt(generatedBill.fuel)]]  : []),
              ...(generatedBill.other > 0 ? [["Other Charges",    "₹ " + fmt(generatedBill.other)]] : []),
              ["Taxable Amount",    "₹ " + fmt(taxable)],
              ...(isIntra
                ? [["CGST @ " + gstRate / 2 + "%", "₹ " + fmt(generatedBill.cgst)],
                   ["SGST @ " + gstRate / 2 + "%", "₹ " + fmt(generatedBill.sgst)]]
                : [["IGST @ " + gstRate + "%",     "₹ " + fmt(generatedBill.igst)]]),
            ].map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "4px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ color: "var(--muted)" }}>{l}</span>
                <span style={{ fontWeight: 700 }}>{v}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "8px 0 0", borderTop: "2px solid var(--teal)", marginTop: 4 }}>
              <span style={{ fontWeight: 800, color: "var(--teal)" }}>Invoice Total</span>
              <span style={{ fontWeight: 800, color: "var(--teal)" }}>₹ {fmt(invTotal)}</span>
            </div>
          </div>

          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 16, paddingTop: 10, borderTop: "1px solid var(--border)", clear: "both" }}>
            <strong>Amount in Words:</strong> {numberToWords(invTotal)}<br />
            <strong>Note:</strong> GST registration: {COMPANY_GSTIN}. This is a computer-generated invoice.
          </div>
        </div>
        <div className="modal-ft">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-amber" onClick={printInvoice}>🖨 Print</button>
        </div>
      </div>
    </div>
  );
}
  
/* ── BUILDER VIEW ── */

  function BuilderView({ onBack, addToast, invoices, setInvoices, openBillCode, autoOpenPdf, onAutoOpenPdfHandled }) {
  /* ── Party list from API ── */
  const [partyList, setPartyList] = useState([]);
  const [partyListLoading, setPartyListLoading] = useState(true);
  const [partyListError, setPartyListError] = useState(null);

  /* ── Selected party & its details from API ── */
  const [partyId, setPartyId] = useState("");
  const [loadedPartyName, setLoadedPartyName] = useState(""); // Party_Name returned directly by get-bill-detail (view mode)
  const [partyDetails, setPartyDetails] = useState(null); // { Party_Id, GST_Number, State_Name, Place_Name }
  const [partyDetailsLoading, setPartyDetailsLoading] = useState(false);

  /* ── Job list (party-dependent, multi-select) from API ── */
  const [selectedJobIds, setSelectedJobIds] = useState([]); // array of job_id (string)
  const [jobList, setJobList] = useState([]);
  const [jobListLoading, setJobListLoading] = useState(false);
  const [jobListError, setJobListError] = useState(null);
  const [jobDropdownOpen, setJobDropdownOpen] = useState(false);
  const jobDropdownRef = useRef(null);


  const [branch, setBranch] = useState("");          // Branch_Id (numeric)

const [branchListLoading, setBranchListLoading] = useState(true);
  const [branchListError, setBranchListError] = useState(null);
const [branchList, setBranchList] = useState([]);   // ← wrong indent
  const [finYearCode, setFinYearCode] = useState(1);  // FinYear_Code
  const [companyCode, setCompanyCode] = useState(1);  // Company_Code
  const [createdBy] = useState(1);                    // Created_By (logged-in user id)
  const [cycle, setCycle] = useState("lastmonth");
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(todayISO());
  const [billDate, setBillDate] = useState(todayISO());
  const [gstRate, setGstRate] = useState(5);
  const [podOnly, setPodOnly] = useState(false);
  const [uninvOnly, setUninvOnly] = useState(true);
  const [delivOnly, setDelivOnly] = useState(false);
  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [tblFilter, setTblFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [consLoaded, setConsLoaded] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedBill, setGeneratedBill] = useState(null);
  const [generatedBillConsignments, setGeneratedBillConsignments] = useState([]); // snapshot at generation time
  const [showSuccessModal, setShowSuccessModal] = useState(false);

const [invTag, setInvTag] = useState("Draft — Not yet saved");
const [viewMode, setViewMode] = useState(false);
const [billDetailLoading, setBillDetailLoading] = useState(false);
const [billStatus, setBillStatus] = useState(null); // "A" = Active, "D" = Deleted (status of the bill loaded via openBillCode)

  /* Guard flag: true while an existing bill is being loaded via openBillCode.
     Prevents the "auto-reset on change" effects below from wiping out the
     party / table data that the bill-detail loader just set. */
  const isLoadingBillRef = useRef(false);
  const pendingJobIdsRef = useRef(null); // job ids parsed from a loaded bill's new_job_id, applied once its job list arrives

  /* Party list no longer depends on Branch at all — it loads once, in
     full, on mount (mirrors the Branch-list effect further below). The
     invoice's Branch is instead DERIVED from whichever party gets picked
     (see the party-details effect right below, which reads
     Billing_Branch_Id back from /get-partydetails and calls setBranch).
     This also sidesteps the old race where a branch-scoped party fetch
     could resolve after — and stomp on — the view-mode bill loader's own
     party selection. */
  useEffect(() => {
    setPartyListLoading(true);
    setPartyListError(null);
    api.get("/get-Billingpartylist")
      .then((res) => setPartyList(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error("Party list fetch error:", err);
        setPartyListError("Failed to load party list");
        addToast("Failed to load party list", "err");
      })
      .finally(() => setPartyListLoading(false));
  }, []);

  /* Reset consignments when key filters change (but not while an existing
     bill is being loaded — its consignment rows come from get-bill-detail
     directly, and from/to/party are all changing at once in that case). */
  useEffect(() => {
    if (isLoadingBillRef.current) return;
    setConsLoaded(false);
    setAllBookings([]);
    setFilteredBookings([]);
  }, [partyId, fromDate, toDate]);
  useEffect(() => {
    if (!partyId) { setPartyDetails(null); return; }
    setPartyDetailsLoading(true);
    api.get(`/get-partydetails/${partyId}`)
      .then((res) => {
        setPartyDetails(res.data);
        // GST_Perc serializes as "gsT_Perc" (the underscore blocks the
        // usual camelCase lowering of the trailing acronym letter) — same
        // quirk as gsT_Number above. Fall back to a couple of alternate
        // casings just in case, then to the current dropdown default.
        const rate = res.data?.gsT_Perc?? res.data?.gsT_Perc ?? res.data?.GST_Perc;
        if (rate !== undefined && rate !== null) {
          setGstRate(Number(rate));
        }

        // Branch is derived FROM the party, not the other way round.
        // billing_Branch_Id hits the same camelCase-of-acronym quirk as
        // gsT_Perc above, so check a couple of casings.
        const derivedBranch =
          res.data?.billing_Branch_Id ??
          res.data?.Billing_Branch_Id ??
          res.data?.billinG_Branch_Id;
        if (derivedBranch !== undefined && derivedBranch !== null) {
          setBranch(String(derivedBranch));
        }
      })
      .catch((err) => {
        console.error("Party details fetch error:", err);
        setPartyDetails(null);
        addToast("Failed to load party details", "err");
      })
      .finally(() => setPartyDetailsLoading(false));
  }, [partyId]);

  /* Job list is party-dependent — reload it whenever partyId changes,
     and clear out any previously selected job so a stale job from the
     old party can't linger. */
  useEffect(() => {
    setSelectedJobIds([]);
    setJobDropdownOpen(false);
    if (!partyId) { setJobList([]); setJobListError(null); return; }

    setJobListLoading(true);
    setJobListError(null);
    api.get("/dropdown", { params: { mode: "BJOB", partyId } })
      .then((res) => {
        setJobList(Array.isArray(res.data) ? res.data : []);

        // If we're loading an existing bill (view/edit via the eye icon),
        // restore its previously saved job selection now that the job
        // list for this party has arrived — doing it here (rather than
        // in the bill-detail handler) avoids the reset above wiping it
        // back out, since that reset always runs first on partyId change.
        if (pendingJobIdsRef.current !== null) {
          setSelectedJobIds(pendingJobIdsRef.current);
          pendingJobIdsRef.current = null;
        }
      })
      .catch((err) => {
        console.error("Job list fetch error:", err);
        setJobList([]);
        setJobListError("Failed to load job list");
        addToast("Failed to load job list", "err");
      })
      .finally(() => setJobListLoading(false));
  }, [partyId]);

  /* Close the Job checkbox dropdown when clicking anywhere outside it. */
  useEffect(() => {
    if (!jobDropdownOpen) return;
    function handleClickOutside(e) {
      if (jobDropdownRef.current && !jobDropdownRef.current.contains(e.target)) {
        setJobDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [jobDropdownOpen]);

useEffect(() => {
  setBranchListLoading(true);
  setBranchListError(null);

    api.get("/dropdown", { params: { mode: "BLB" } })
  .then((res) => setBranchList(res.data))
    .catch((err) => {
      console.error("Branch list fetch error:", err);
      setBranchListError("Failed to load branch list");
      addToast("Failed to load branch list", "err");
    })
    .finally(() => setBranchListLoading(false));

}, []);
  const invNo = billDetailLoading
  ? "Loading…"
  : generatedBill?.billNo || "Draft — pending generation";
  const gstType = partyDetails?.gsT_Number ? getGstType(partyDetails.gsT_Number) : "";
  const selectedPartyName = loadedPartyName || partyList.find((p) => String(p.party_id) === String(partyId))?.party_Name || "";

  const applyFilters = useCallback((bookings, f) => {
    return bookings.filter((b) => {
      if (!viewMode && uninvOnly && b.invoiced) return false;
      if (f === "pod" && !b.pod) return false;
      if (f === "nopod" && b.pod) return false;
      if (searchQ && !b.cnr.toLowerCase().includes(searchQ.toLowerCase())) return false;
      // Include Previous Unbilled reaches back before fromDate on purpose —
      // only enforce the fromDate lower bound when that toggle is off.
      if (!viewMode && !unbilledOnly && fromDate && b.date < fromDate) return false;
      if (!viewMode && toDate && b.date > toDate) return false;
      return true;
    });
  }, [viewMode, uninvOnly, unbilledOnly, searchQ, fromDate, toDate]);

  useEffect(() => {
    setFilteredBookings(applyFilters(allBookings, tblFilter));
  }, [allBookings, tblFilter, viewMode, podOnly, uninvOnly, delivOnly, unbilledOnly, searchQ, fromDate, toDate, applyFilters]);

  function handleCycleChange(c) {
    setCycle(c);
    const today = new Date();
    let from, to;
    if (c === "thismonth") { from = new Date(today.getFullYear(), today.getMonth(), 1); to = new Date(today.getFullYear(), today.getMonth() + 1, 0); }
    else if (c === "lastmonth") { from = new Date(today.getFullYear(), today.getMonth() - 1, 1); to = new Date(today.getFullYear(), today.getMonth(), 0); }
    else if (c === "thisweek") { const d = today.getDay(); from = new Date(today); from.setDate(today.getDate() - d + 1); to = new Date(from); to.setDate(from.getDate() + 6); }
    else if (c === "lastweek") { const d = today.getDay(); from = new Date(today); from.setDate(today.getDate() - d - 6); to = new Date(from); to.setDate(from.getDate() + 6); }
    else return;
    setFromDate(toLocalISO(from));
    setToDate(toLocalISO(to));
  }

  const [consLoading, setConsLoading] = useState(false);

  async function loadConsignments() {
    /* ── Edit mode: fetch this bill's own consignments, bill-id wise.
       No party / date / filter checks needed — the bill code already
       identifies exactly what to fetch. ── */
    if (viewMode && generatedBill?.billId) {
      setConsLoading(true);
      try {
        const res = await api.get("/GETDataBillCodeWise", {
          params: { bill_code: generatedBill.billId },
        });
        const raw = res.data;

        const bookings = raw.map((x, i) => ({
          id:        x.consignment_No || i,
          cnr:       x.consignment_No || "—",
          date:      x.book_Dt ? x.book_Dt.slice(0, 10) : "",
          dateDisp:  x.book_Dt ? new Date(x.book_Dt).toLocaleDateString("en-IN") : "—",
          from:      x.book_Place      || "—",
          to:        x.delivery_Place  || "—",
          pcs:       x.no_of_pcs       || 0,
          wt:        parseFloat(x.charged_weight)  || 0,
          freight:   parseFloat(x.freight_Charges) || 0,
          fuel:      0,
          other:     parseFloat(x.other_charges)   || 0,
          subtotal:  parseFloat(x.taxable_Amt || 0),
          pod:       x.pod_Available === "Y",
          status:    x.delivery_Status  || "Pending",
          invoiced:  x.invoice_Status  === "Invoiced",
          selected:  true,
        }));

        setAllBookings(bookings);
        setConsLoaded(true);
        addToast(`Loaded ${bookings.length} consignment${bookings.length !== 1 ? "s" : ""} for this bill`, "ok");
      } catch (err) {
        console.error("Bill-wise consignment load error:", err);
        addToast("Failed to load consignments for this bill", "err");
      } finally {
        setConsLoading(false);
      }
      return;
    }

    if (!partyId)    { addToast("Select a party first", "warn");  return; }
    if (!fromDate)   { addToast("Select a From Date", "warn");    return; }
    if (!toDate)     { addToast("Select a To Date", "warn");      return; }

    setConsLoading(true);
    try {
      const params = new URLSearchParams({
        fromDate,
        toDate,
        partyId,
        PODAvailableOnly: podOnly   ? "Y" : "N",
        UninvoicedOnly:   uninvOnly ? "Y" : "N",
        DeliveredOnly:    delivOnly ? "Y" : "N",
        UnbilledOnly:     unbilledOnly ? "Y" : "N",
      });

      const res = await api.get(`/consignment-status-report?${params}`);
      const raw = res.data;

      /* Map backend fields → table field names */
      const bookings = raw.map((x, i) => ({
        id:        x.consignment_No || i,
        cnr:       x.consignment_No || "—",
        date:      x.book_Dt ? x.book_Dt.slice(0, 10) : "",
        dateDisp:  x.book_Dt ? new Date(x.book_Dt).toLocaleDateString("en-IN") : "—",
        from:      x.book_Place      || "—",
        to:        x.delivery_Place  || "—",
        pcs:       x.no_of_pcs       || 0,
        wt:        parseFloat(x.charged_weight)  || 0,
        freight:   parseFloat(x.freight_Charges) || 0,
        fuel:      0,
        other:     parseFloat(x.other_charges)   || 0,
        subtotal:  parseFloat(x.taxable_Amt || 0),
        pod:       x.pod_Available === "Y",
        status:    x.delivery_Status  || "Pending",
        invoiced:  x.invoice_Status  === "Invoiced",
        selected:  false,
      }));

      setAllBookings(bookings);
      setConsLoaded(true);
      addToast(`Loaded ${bookings.length} consignment${bookings.length !== 1 ? "s" : ""} for ${selectedPartyName}`, "ok");
    } catch (err) {
      console.error("Consignment load error:", err);
      addToast("Failed to load consignments", "err");
    } finally {
      setConsLoading(false);
    }
  }

  function toggleRow(idx, checked) {
    setFilteredBookings((prev) => prev.map((b, i) => i === idx ? { ...b, selected: checked } : b));
  }

  function toggleAll(checked) {
    setFilteredBookings((prev) => prev.map((b) => ({ ...b, selected: checked })));
  }

  async function handleGenerate() {
    // Edit mode + bill still Active → block regeneration until it's deleted first.
    if (viewMode && billStatus === "A") {
      addToast("Please delete this bill before regenerate", "warn");
      return;
    }

    const sel = filteredBookings.filter((b) => b.selected);
    if (!sel.length)  { addToast("Select at least one consignment", "warn"); return; }
    if (!partyId)     { addToast("Select a party first", "warn");            return; }
    if (!branch)      { addToast("Select a branch first", "warn");           return; }

    const taxable    = sel.reduce((a, b) => a + b.freight, 0);
    const fuelAmt    = sel.reduce((a, b) => a + (b.fuel  || 0), 0);
    const otherAmt   = sel.reduce((a, b) => a + (b.other || 0), 0);
    const isIntra    = gstType === "CGST+SGST";

    // Same basis the Invoice Summary panel uses (sum of each consignment's
    // subtotal, NOT just freight+fuel+other — subtotal can include other
    // components those three don't individually capture). GST, Bill_Amount
    // and Bill_Total must all be derived from this same figure or they'll
    // drift from what's shown on screen.
    const taxableAmount = +sel.reduce((a, b) => a + b.subtotal, 0).toFixed(2);
    const gst           = calcGst(taxableAmount, gstRate, gstType);
    const invoiceTotal  = +(taxableAmount + gst.total).toFixed(2);

    const payload = {
      Party_ID:          parseInt(partyId),
      Branch_Id:         parseInt(branch),
      FinYear_Code:      finYearCode,
      Company_Code:      companyCode,
      Created_By:        createdBy,
      Freight_Charges:   taxable,
      FuelSurchargeAmt:  fuelAmt,
      Other_Charges:     otherAmt,
      CGST:              isIntra ? gst.cgst : 0,
      SGST:              isIntra ? gst.sgst : 0,
      IGST:              isIntra ? 0        : gst.igst,
      Bill_Amount:       taxableAmount,
      Bill_Total:        invoiceTotal,
      ConsignmentNos:    sel.map((b) => parseInt(b.id)),
      Job_Ids:           selectedJobIds.map((id) => parseInt(id, 10)),
      Bill_From_Date:    fromDate,   // "YYYY-MM-DD" from date picker
      Bill_to_Date:      toDate,     // "YYYY-MM-DD" from date picker
      Bill_Date:         billDate,   // "YYYY-MM-DD" bill date
    };

    setGenerating(true);
    try {
      const res = await api.post("/generate-bill", payload);
      const data = res.data;
// In handleGenerate(), update this section:
if (data.success) {
  setGeneratedBillConsignments(sel);

  setGeneratedBill({
    billId:        data.billId,
    billNo:        data.billNo,
    message:       data.message,
    isRegenerated: data.isRegenerated || false,   // ← ADD THIS
    freight:  taxable,
    fuel:     fuelAmt,
    other:    otherAmt,
    taxable:  taxableAmount,
    cgst:     isIntra ? gst.cgst : 0,
    sgst:     isIntra ? gst.sgst : 0,
    igst:     isIntra ? 0 : gst.igst,
    gstTotal: gst.total,
    total:    invoiceTotal,
  });
  setShowSuccessModal(true);
  setInvTag(data.isRegenerated ? `♻ ${data.billNo} (Regenerated)` : `✓ ${data.billNo}`);
  addToast(`Bill ${data.isRegenerated ? "Regenerated" : "Generated"}: ${data.billNo}`, "ok");
  // ... rest unchanged
   

        /* Update invoices list */
        setInvoices((prev) => [{
          no:        data.billNo,
          billId:    data.billId,
          party:     partyId,
          partyName: selectedPartyName,
          gstNo:     partyDetails?.gsT_Number || "—",
          period:    fromDate + " – " + toDate,
          cnrs:      sel.length,
          freight:   +(taxable + fuelAmt + otherAmt).toFixed(2),
          gst:       gst.total,
          total:     +(taxable + fuelAmt + otherAmt + gst.total).toFixed(2),
          gstType:   isIntra ? "CGST+SGST" : "IGST",
          status:    "draft",
        }, ...prev]);

        /* Mark selected consignments as invoiced */
        setAllBookings((prev) => prev.map((b) =>
          b.selected ? { ...b, invoiced: true, selected: false } : b
        ));
      } else {
        addToast(data.message || "Failed to generate bill", "err");
      }
    } catch (err) {
      console.error("Generate bill error:", err);
      const msg = err.response?.data?.message || "Server error generating bill";
      addToast(msg, "err");
    } finally {
      setGenerating(false);
    }
  }

  function handleSaveInvoice() {
    /* Bill already saved to DB via handleGenerate → just close the PDF modal */
    setShowPdf(false);
    if (generatedBill) addToast(`Bill ${generatedBill.billNo} already saved`, "info");
    else addToast("Generate the invoice first", "warn");
  }

  const partyInfo = partyDetails;
  const partyState = partyDetails?.gsT_Number?.slice(0, 2);
  const isIntra = gstType === "CGST+SGST";

  /* ── Load existing invoice when opened from register ── */
  useEffect(() => {
    if (!openBillCode) return;

    isLoadingBillRef.current = true;
    setBillDetailLoading(true);
    setViewMode(true);
    setInvTag("Loading invoice…");

    // These filters only make sense while searching for consignments to add
    // to a NEW invoice. They don't apply in view/edit mode (we always load
    // this bill's own consignments), so reset them here rather than leaving
    // "Uninvoiced Only" ticked from its create-mode default — that forced
    // the user to manually uncheck it before Load Consignments would work.
    setPodOnly(false);
    setUninvOnly(false);
    setDelivOnly(false);
    setUnbilledOnly(false);

    api.get("/get-bill-detail", { params: { billCode: openBillCode } })
      .then((res) => {
        const d = res.data;

        const billBranch = String(d.branch_Id ?? "");
        const billPartyId = String(d.party_ID ?? "");

        setBranch(billBranch);
        setBillStatus(d.bill_Status ?? d.Bill_Status ?? null);
        if (d.bill_From_Date) setFromDate(d.bill_From_Date.slice(0, 10));
        if (d.bill_to_Date)   setToDate(d.bill_to_Date.slice(0, 10));
        if (d.bill_Date)      setBillDate(d.bill_Date.slice(0, 10));
        // Stage the saved job selection (stored as "1,3,7,23," in new_job_id)
        // before setPartyId below triggers the job-list-loading effect —
        // that effect will apply this once the job list for the bill's
        // party has actually loaded.
        pendingJobIdsRef.current = (d.new_job_id || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        setPartyId(billPartyId);
        setLoadedPartyName(d.party_Name || "");

        // No branch-scoped party fetch needed here any more — the party
        // list loads once, in full, on mount, so it already contains this
        // bill's party regardless of branch. setPartyId above is enough to
        // make the dropdown match it, and the party-details effect will
        // re-derive `branch` from the party too (redundant with billBranch
        // above, but harmless — they agree).

        const freight = parseFloat(d.freight_Charges) || 0;
        if (freight > 0) {
          const totalGst = (parseFloat(d.cgst) || 0) + (parseFloat(d.sgst) || 0) + (parseFloat(d.igst) || 0);
          const rate = Math.round((totalGst / freight) * 100);
          if ([5, 12, 18].includes(rate)) setGstRate(rate);
        }

        const rows = (d.consignments || []).map((c, i) => {
          const freight  = parseFloat(c.freight_Charges) || 0;
          const other    = parseFloat(c.other_charges)   || 0;
          const fuel     = 0;
          const subtotal = +(freight + fuel + other).toFixed(2);
          return {
            id:        c.consignment_No || i,
            cnr:       c.consignment_No ? String(c.consignment_No) : `CNR-${i + 1}`,
            dateDisp:  c.book_Dt ? new Date(c.book_Dt).toLocaleDateString("en-IN") : "—",
            from:      c.book_Place     || "—",
            to:        c.delivery_Place || "—",
            pcs:       c.no_of_pcs      || 0,
            wt:        parseFloat(c.charged_weight) || 0,
            freight, fuel, other, subtotal,
            pod:       false,
            status:    "Delivered",
            selected:  true,
            refNo:     c.ref_No         || "",
            consignor: c.consignor_Name || "",
            consignee: c.consignee_Name || "",
          };
        });

        setAllBookings(rows);
        setFilteredBookings(rows);
        setConsLoaded(true);
        setGeneratedBillConsignments(rows);

const freightAmt = parseFloat(d.freight_Charges) || 0;
const fuelAmt    = parseFloat(d.fuelSurchargeAmt) || 0;
const otherAmt   = parseFloat(d.other_Charges) || 0;

setGeneratedBill({
  billNo:   d.bill_No,
  billCode: openBillCode,
  billId:   d.bill_Id,
  freight:  freightAmt,
  fuel:     fuelAmt,
  other:    otherAmt,
  taxable:  +(freightAmt + fuelAmt + otherAmt).toFixed(2),
  cgst:     parseFloat(d.cgst) || 0,
  sgst:     parseFloat(d.sgst) || 0,
  igst:     parseFloat(d.igst) || 0,
  gstTotal: (parseFloat(d.cgst)||0) + (parseFloat(d.sgst)||0) + (parseFloat(d.igst)||0),
  total:    parseFloat(d.bill_Total) || 0,
  isRegenerated: false,
});        setInvTag(d.bill_No || "Loaded Invoice");
        addToast(`Invoice ${d.bill_No} loaded`, "ok");

        if (autoOpenPdf) {
          setShowPdf(true);
          if (onAutoOpenPdfHandled) onAutoOpenPdfHandled();
        }
      })
      .catch(() => {
        addToast("Failed to load invoice details", "err");
        setInvTag("Error loading invoice");
      })
      .finally(() => {
        setBillDetailLoading(false);
        // Release the guard once this update's effects (branch/party-list
        // fetch, consignment reset) have had a chance to run and bail out
        // early. A macrotask delay is enough since React flushes passive
        // effects from the state update above before the next timeout fires.
        setTimeout(() => { isLoadingBillRef.current = false; }, 0);
      });
  }, [openBillCode]);

  return (
    <div className="page">
      <div className="pg-row">
        <div className="pg-title">
          <h1>New Invoice</h1>
          <p>Select party, period and consignments to generate a GST-compliant invoice</p>
        </div>
        <div className="pg-acts">
          <button className="btn btn-ghost btn-sm" onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Back to List
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => addToast("Printing…", "info")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
            Print
          </button>
          <button
            className="btn btn-red btn-sm"
            onClick={() => {
              if (!generatedBill) {
                addToast("Generate the bill first before viewing the PDF", "warn");
                return;
              }
              setShowPdf(true);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            PDF
          </button>
        </div>
      </div>

      {/* Invoice Number Banner */}
      <div className="inv-no-banner">
        <div>
          <div className="inv-no-lbl">Invoice Number</div>
          <div className="inv-no-val">{invNo}</div>
          <div className="inv-no-tag">{invTag}</div>
        </div>
        <div className="inv-meta">
          <div className="inv-meta-item">
            <div className="inv-meta-lbl">Invoice Date</div>
            <div className="inv-meta-val">14 Apr 2026</div>
          </div>
          <div className="inv-meta-item">
            <div className="inv-meta-lbl">Due Date</div>
            <div className="inv-meta-val">14 May 2026</div>
          </div>
          <div className="inv-meta-item">
            <div className="inv-meta-lbl">GST Type</div>
            <div className="inv-meta-val">{gstType || "—"}</div>
          </div>
        </div>
      </div>

      <div className="inv-layout">
        {/* LEFT */}
        <div>
          {/* Filter Card */}
          <div className="card">
            <div className="band teal">
              <div className="band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
              </div>
              <div>
                <div className="band-title">Invoice Parameters</div>
                <div className="band-sub">Select party, period and filters to load consignments</div>
              </div>
            </div>
            <div className="card-body">
              <div className="g3" style={{ marginBottom: 12 }}>

                <div className="field">
  <label>Billing Branch <span className="req">*</span></label>
  <select
    value={branch}
    onChange={(e) => {
      // Manual override only — normally this field fills itself in from
      // the selected party's own Billing_Branch_Id (see the party-details
      // effect). Party is no longer reset/refetched here: the party list
      // is independent of branch now, so changing branch shouldn't touch it.
      setBranch(e.target.value);
    }}
    disabled={branchListLoading}
  >
    <option value="">
      {branchListLoading ? "Loading branches..." : "— Select Branch —"}
    </option>
    {branchList.map((b) => (
      <option key={b.Branch_id} value={b.Branch_id}>
        {b.Branch_Name}
      </option>
    ))}
  </select>
  {branchListError && (
    <span style={{ fontSize: 11, color: "var(--red)", marginTop: 2 }}>
      ⚠ {branchListError}
    </span>
  )}
</div>
                <div className="field">
                  <label>Billing Party <span className="req">*</span></label>
                  <select
                    value={partyId}
                    onChange={(e) => {
                      setPartyId(e.target.value);
                      setConsLoaded(false);
                      setAllBookings([]);
                      setFilteredBookings([]);
                    }}
  disabled={partyListLoading}
                  >
             <option value="">
  {partyListLoading
    ? "Loading parties…"
    : partyListError
    ? "Error loading parties"
    : "— Select Billing Party —"}
</option>
                    {partyList.map((p) => (
                      <option key={p.party_id} value={p.party_id}>{p.party_Name}</option>
                    ))}
                  </select>
                  {partyListError && (
                    <span style={{ fontSize: 11, color: "var(--red)", marginTop: 2 }}>⚠ {partyListError}</span>
                  )}
                </div>

                <div className="field" ref={jobDropdownRef} style={{ position: "relative" }}>
                  <label>Job</label>
                  <button
                    type="button"
                    onClick={() => {
                      if (!partyId || jobListLoading) return;
                      setJobDropdownOpen((o) => !o);
                    }}
                    disabled={!partyId || jobListLoading}
                    style={{
                      /* Matches the look of .field select exactly, since a plain
                         <button> doesn't pick up that element-scoped CSS rule. */
                      width: "100%",
                      padding: "8px 30px 8px 11px",
                      border: "1px solid var(--border, #e2e8f0)",
                      borderRadius: 6,
                      fontFamily: "inherit",
                      fontSize: 13,
                      textAlign: "left",
                      color: !partyId ? "var(--muted, #94a3b8)" : "var(--ink, #1e293b)",
                      background: !partyId || jobListLoading ? "var(--s3, #f1f5f9)" : "var(--surface, #fff)",
                      outline: "none",
                      cursor: !partyId || jobListLoading ? "not-allowed" : "pointer",
                      position: "relative",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {!partyId
                      ? "— Select a party first —"
                      : jobListLoading
                      ? "Loading jobs…"
                      : jobListError
                      ? "Error loading jobs"
                      : jobList.length === 0
                      ? "— No jobs for this party —"
                      : selectedJobIds.length === 0
                      ? "— Select Job —"
                      : selectedJobIds.length === 1
                      ? (jobList.find((j) => String(j.job_id) === selectedJobIds[0])?.job_name || "1 job selected")
                      : `${selectedJobIds.length} jobs selected`}
                    <span
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: jobDropdownOpen ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
                        pointerEvents: "none",
                        color: "#94a3b8",
                        fontSize: 10,
                      }}
                    >
                      ▾
                    </span>
                  </button>

                  {jobDropdownOpen && partyId && !jobListLoading && jobList.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        minWidth: "100%",
                        width: "max-content",
                        maxWidth: 420,
                        zIndex: 30,
                        background: "var(--surface, #fff)",
                        border: "1px solid var(--border, #e2e8f0)",
                        borderRadius: 6,
                        boxShadow: "var(--sh-md, 0 2px 8px rgba(0,0,0,0.1))",
                        maxHeight: 260,
                        overflowY: "auto",
                        padding: "6px 4px",
                      }}
                    >
                      {jobList.map((j) => {
                        const idStr = String(j.job_id);
                        const checked = selectedJobIds.includes(idStr);
                        const toggle = () =>
                          setSelectedJobIds((prev) =>
                            checked ? prev.filter((x) => x !== idStr) : [...prev, idStr]
                          );
                        return (
                          <div
                            key={j.job_id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 8px",
                              fontSize: 13,
                              fontWeight: 400,
                              textTransform: "none",
                              color: "inherit",
                              borderRadius: 4,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <input
                              type="checkbox"
                              className="chk"
                              checked={checked}
                              onChange={toggle}
                              style={{
                                /* .field input (0,1,1) otherwise beats .chk (0,1,0)
                                   and forces width:100%/padding/appearance:none,
                                   which is what turned these into big blank boxes.
                                   Inline styles always win, so restore the real
                                   checkbox look here. */
                                width: 15,
                                height: 15,
                                minWidth: 15,
                                padding: 0,
                                margin: 0,
                                flexShrink: 0,
                                border: "1px solid var(--border)",
                                borderRadius: 3,
                                appearance: "auto",
                                WebkitAppearance: "auto",
                                accentColor: "var(--blue)",
                                background: "none",
                                boxShadow: "none",
                                cursor: "pointer",
                              }}
                            />
                            <span style={{ cursor: "pointer" }} onClick={toggle}>
                              {j.job_name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {jobListError && (
                    <span style={{ fontSize: 11, color: "var(--red)", marginTop: 2 }}>⚠ {jobListError}</span>
                  )}
                </div>

                <div className="field">
                  <label>Bill Date <span className="req">*</span></label>
                  <input
                    type="date"
                    value={billDate}
                    onChange={(e) => setBillDate(e.target.value)}
                  />
                </div>

                <div className="field">
                  <label>Billing Cycle</label>
                  <select value={cycle} onChange={(e) => handleCycleChange(e.target.value)}>
                    <option value="custom">Custom Period</option>
                    <option value="thismonth">This Month</option>
                    <option value="lastmonth">Last Month</option>
                    <option value="thisweek">This Week</option>
                    <option value="lastweek">Last Week</option>
                  </select>
                </div>
                <div className="field">
                  <label>From Date <span className="req">*</span></label>
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                <div className="field">
                  <label>To Date <span className="req">*</span></label>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
                <div className="field">
                  <label>GST Rate</label>
                  <select value={gstRate} onChange={(e) => setGstRate(parseFloat(e.target.value))}>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="0">0% (Exempt)</option>
                  </select>
                </div>
              </div>

              {/* Party GST Info */}
              {partyId && (
                <div style={{ marginBottom: 12 }}>
                  {partyDetailsLoading ? (
                    <div className="party-strip" style={{ justifyContent: "center", padding: "16px 14px" }}>
                      <span style={{ fontSize: 12.5, color: "var(--muted)" }}>⏳ Loading party details…</span>
                    </div>
                  ) : partyInfo ? (
                    <div className="party-strip">
                      <div>
                        <div className="ps-cell-lbl">Party</div>
                        <div className="ps-cell-val">{selectedPartyName}</div>
                        <div className="ps-cell-sub">ID: {partyInfo.party_Id}</div>
                      </div>
                      <div>
                        <div className="ps-cell-lbl">Party GSTIN</div>
                        <div className="ps-cell-val">{partyInfo.gsT_Number || "—"}</div>
                        <div className="ps-cell-sub">State: {partyInfo.state_Name || "—"}{partyState ? ` (${partyState})` : ""}</div>
                      </div>
                      <div>
                        <div className="ps-cell-lbl">Billing Branch</div>
                        <div className="ps-cell-val">{partyInfo.place_Name || "—"}</div>
                        <div style={{ marginTop: 4 }}>
                          <span className={`gst-type-display ${isIntra ? "gst-intra" : "gst-inter"}`}>
                            {isIntra ? "🔵 Intra-State" : "🟡 Inter-State"}&nbsp;{gstType}
                          </span>
                        </div>
                        <div className="ps-cell-sub" style={{ marginTop: 4 }}>
                          {isIntra ? "CGST + SGST applicable (same state)" : "IGST applicable (different states)"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="party-strip" style={{ justifyContent: "center", padding: "14px" }}>
                      <span style={{ fontSize: 12, color: "var(--red)" }}>⚠ Could not load party details</span>
                    </div>
                  )}
                </div>
              )}

              {/* Filter Options */}
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 14, padding: "10px 14px", background: "#eef2ff", borderRadius: 6, border: "1px solid rgba(37,99,235,.15)" }}>
                <span className="sec-lbl" style={{ margin: 0 }}>Filter Options</span>
                {[
                  [podOnly, setPodOnly, "POD Available Only", true, null],
                  [uninvOnly, setUninvOnly, "Uninvoiced Only", false, "Also includes consignments whose bill was deleted"],
                  [delivOnly, setDelivOnly, "Delivered Only", false, "Currently loads all consignments regardless of delivery status"],
                  [unbilledOnly, setUnbilledOnly, "Include Previous Unbilled", false, "Pulls in never-billed consignments from before your From Date too — bounded only by To Date"],
                ].map(([val, setter, label, recommended, hint]) => (
                  <label key={label} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                    <label className="sw">
                      <input type="checkbox" checked={val} onChange={(e) => setter(e.target.checked)} />
                      <span className="sw-track"></span>
                    </label>
                    {label}
                    {recommended && <span className="filter-tag">Recommended</span>}
                    {hint && <span className="hint-badge" title={hint}>i</span>}
                  </label>
                ))}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={loadConsignments}
                  disabled={consLoading || (!viewMode && (!partyId || !fromDate || !toDate))}
                  style={{ marginLeft: "auto" }}
                >
                  {consLoading ? (
                    <>⏳ Loading…</>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                      </svg>
                      Load Consignments
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Consignment Table */}
          {consLoaded && (
            <ConsignmentTable
              bookings={filteredBookings}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              tblFilter={tblFilter}
              setTblFilter={setTblFilter}
              searchQ={searchQ}
              setSearchQ={setSearchQ}
            />
          )}
        </div>

        {/* RIGHT: Invoice Summary */}
        <div>
          {consLoaded ? (
            <InvoiceSummary
              bookings={filteredBookings}
              gstRate={gstRate}
              gstType={gstType}
              invNo={invNo}
              generating={generating}
              onGenerate={handleGenerate}
              onPrint={() => addToast("Printing…", "info")}
              onPdf={() => setShowPdf(true)}
              regenerateLocked={viewMode && billStatus === "A"}
            />
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div className="band">
                <div className="band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>
                </div>
                <div className="band-title">Invoice Summary</div>
              </div>
              <div className="empty-state" style={{ padding: "32px 20px" }}>
                <div className="empty-icon">📋</div>
                <div className="empty-title">No consignments loaded</div>
                <div className="empty-sub">Select a party and period, then click Load Consignments</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Bill Generated Success Modal ── */}
      {showSuccessModal && generatedBill && (
        <div className="overlay show" onClick={() => setShowSuccessModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-hd" style={{ background: "linear-gradient(135deg,#16a34a,#15803d)", color: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="22" height="22">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
      

<span style={{ fontWeight: 700, fontSize: 16 }}>
  {generatedBill?.isRegenerated ? "Bill Regenerated Successfully" : "Bill Generated Successfully"}
</span>
              </div>
              <button onClick={() => setShowSuccessModal(false)} style={{ background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: "28px 28px 24px" }}>
              {/* Bill No highlight */}
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted)", marginBottom: 6 }}>Bill Number</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "var(--navy)", letterSpacing: 1, background: "var(--blue-lt)", borderRadius: 10, padding: "14px 24px", border: "2px solid var(--blue)", display: "inline-block" }}>
                  {generatedBill.billNo}
                </div>
              </div>
              {/* Details grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", background: "var(--s2)", borderRadius: 8, padding: "14px 16px", marginBottom: 20, border: "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5 }}>Bill ID</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--navy)", marginTop: 2 }}>#{generatedBill.billId}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5 }}>Party</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", marginTop: 2 }}>{selectedPartyName}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5 }}>Period</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", marginTop: 2 }}>{fromDate} – {toDate}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5 }}>Consignments</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--blue)", marginTop: 2 }}>
                    {filteredBookings.filter((b) => b.invoiced).length} CNRs
                  </div>
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5 }}>GST Type</div>
                  <div style={{ marginTop: 4 }}>
                    <span className={`gst-type-display ${isIntra ? "gst-intra" : "gst-inter"}`}>
                      {isIntra ? "🔵 CGST + SGST" : "🟡 IGST"} @ {gstRate}%
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowSuccessModal(false)}>
                  Close
                </button>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { setShowSuccessModal(false); setShowPdf(true); }}>
                  📄 View PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* PDF Modal */}
      <PdfModal
        open={showPdf}
        onClose={() => setShowPdf(false)}
        consignments={generatedBillConsignments}
        partyId={partyId}
        partyName={selectedPartyName}
        partyDetails={partyDetails}
        gstType={gstType}
        gstRate={gstRate}
        generatedBill={generatedBill}
        onPrint={() => { window.print(); addToast("Sent to printer", "info"); }}
      />
    </div>
  );
}

/* ════════════════════════════════════════════
   ROOT COMPONENT
════════════════════════════════════════════ */
export default function PartyInvoice() {
  const [view, setView] = useState("list");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [openBillCode, setOpenBillCode] = useState(null);
  const [autoOpenPdf, setAutoOpenPdf] = useState(false);

  /* ── Filter state for Invoice Register ── */
  const now = new Date();
  const firstOfMonth = toLocalISO(new Date(now.getFullYear(), now.getMonth(), 1));
  const today = toLocalISO(now);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterFrom, setFilterFrom]   = useState(firstOfMonth);
  const [filterTo, setFilterTo]       = useState(today);
  const [branchList, setBranchList]   = useState([]);

  function addToast(msg, type = "info") {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  }

  /* ── Fetch branch list for filter dropdown ── */
  useEffect(() => {
    api.get("/dropdown", { params: { mode: "BLB" } })
      .then((res) => setBranchList(
        res.data.map((b) => ({ branch_id: b.Branch_id, branch_Name: b.Branch_Name }))
      ))
      .catch(() => {});
  }, []);

  /* ── Fetch invoice register from API ── */
  function fetchInvoices() {
    setLoading(true);
    setLoadError(null);
    const params = {};
    if (filterBranch) params.branchId  = filterBranch;
    if (filterFrom)   params.fromDate  = filterFrom;
    if (filterTo)     params.toDate    = filterTo;
    api
      .get("/bill-summary", { params })
      .then((res) => setInvoices(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        const msg = err?.response?.data?.Message || err.message || "Unknown error";
        setLoadError(msg);
        addToast("Failed to load invoices: " + msg, "err");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (view !== "list") return;
    fetchInvoices();
  }, [view]);

  function handleOpenInvoice(billCode) {
    setOpenBillCode(billCode);
    setView("builder");
    addToast("Opening invoice " + billCode, "info");
  }

  function handleOpenInvoicePdf(billCode) {
    setAutoOpenPdf(true);
    setOpenBillCode(billCode);
    setView("builder");
    addToast("Loading invoice preview…", "info");
  }

  return (
    <>
      <Header view={view} />
      <Breadcrumb view={view} />

      {view === "list" ? (
  <ListView
  invoices={invoices}
  loading={loading}
  loadError={loadError}
  onNew={() => setView("builder")}
  onOpen={handleOpenInvoice}
  onOpenPdf={handleOpenInvoicePdf}
  addToast={addToast}
  branchList={branchList}
  filterBranch={filterBranch}
  setFilterBranch={setFilterBranch}
  filterFrom={filterFrom}
  setFilterFrom={setFilterFrom}
  filterTo={filterTo}
  setFilterTo={setFilterTo}
  onRefresh={fetchInvoices}
/>
      ) : (

<BuilderView
  onBack={() => { setView("list"); setOpenBillCode(null); setAutoOpenPdf(false); }}
  addToast={addToast}
  invoices={invoices}
  setInvoices={setInvoices}
  openBillCode={openBillCode}
  autoOpenPdf={autoOpenPdf}
  onAutoOpenPdfHandled={() => setAutoOpenPdf(false)}
/>
        
      )}

      <ToastContainer toasts={toasts} />
    </>
  );
}
